// @givmo/board — every word the Schedule O draft page says in Board's own voice (CBG-YR5-SCHO; the
// derivation §4, frame p8 and the states no frame drew; the backend's `board-schedule-o.md` §W1).
//
// Every line is approved, as built: Jonathan approved them in copy round CBG-YR5-COPY1's next part
// (2026-10-16 19:35 PDT, "go with your recs", on the copy page
// `scratchpad/2026-10-16-scho-copy-round/copy-review.html`). A line reused from another screen says
// which. A line added or changed since is marked DRAFT where it sits, until its own round.
//
// ⚠ NEITHER THE DRAFT'S SENTENCES NOR THE IRS'S WORDS ARE HERE. Each entry's text comes from the read,
// built by the backend from the board's records alone (`app/domain/board_schedule_o.py`), and each IRS
// sentence comes from the line map, quoted with its source (D-CBG-30). This file is the page's frame:
// its title, "Copy", "From your records:" and its state lines. It keeps the Form 990 page's bans: no
// answer, no check, no score (`schedule-o-words.test.ts`).

/** Up to ten in words, 11 and over in digits: the backend's own rule (`number_text`). */
const NUMBER_WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
] as const;

export function numberWord(count: number): string {
  return (Number.isInteger(count) ? NUMBER_WORDS[count] : undefined) ?? String(count);
}

export const scheduleOWords = {
  // Drawn p8.
  pageTitle: 'Schedule O draft',
  // Drawn p8 ("2026 Form 990 · Sentences built only …"): the tax year's named year.
  lead: (namedYear: number) =>
    `${namedYear} Form 990 · Sentences built only from what your board recorded, for you to edit into your own words.`,
  // Drawn p8.
  copy: 'Copy',
  // Not drawn: the toast once an entry's text is on the clipboard.
  copied: 'Copied.',
  // Not drawn: the clipboard refused; the text is on screen and selectable.
  copyRefused: 'We couldn’t copy it — you can select the text and copy it yourself.',
  // Drawn p8.
  fromYourRecords: 'From your records:',
  // Drawn p8 ("The four meetings"): line 8a opened on the Form 990 page.
  theMeetings: (count: number) =>
    count === 1 ? 'The meeting' : `The ${numberWord(count)} meetings`,
  // Drawn p8 ("The two written consents"): line 8a opened on the Form 990 page.
  theConsents: (count: number) =>
    count === 1 ? 'The written consent' : `The ${numberWord(count)} written consents`,
  // Drawn p8 ("The copy provided Apr 1, 2027"): the copy's receipts.
  theCopyProvided: (day: string) => `The copy provided ${day}`,
  // Drawn p8 ("The April 8 meeting’s minutes"): the board's or a committee's meeting.
  theMeetingsMinutes: (monthDay: string) => `The ${monthDay} meeting’s minutes`,
  // Not drawn: the review's meeting when it has no minutes yet (a committee's, often).
  theMeeting: (monthDay: string) => `The ${monthDay} meeting`,
  // Not drawn: the review recited is a written consent, named by the date it bears.
  theConsentDated: (day: string | null) =>
    day === null ? 'The written consent' : `The written consent dated ${day}`,
  // Not drawn: line 8b with no meeting whose minutes exist and no consent.
  noMeetingsOrConsents:
    'Board holds no record of the board’s meetings or written consents in this tax year.',
  // Not drawn (the derivation's words): line 11b with no copy, beside the quotation.
  noCopy: 'Board holds no record of this return being provided or reviewed.',
  // Not drawn: line 11b where every copy of the year's return was withdrawn.
  everyCopyWithdrawn: 'Each copy of this return provided to the board was withdrawn.',
  // Not drawn: line 11b with a copy that reached no director and no review (a review named
  // neutrally, the board's or a committee's).
  copyReachedNoOne: 'Board holds no record of this copy reaching a director, or of a review.',
  // Not drawn: no tax year (no fiscal year end recorded or chosen); a link there.
  noTaxYear: 'Choose the day your tax year ends on the Form 990 page.',
  // Not drawn: an address the read refused (a stale year, a day it can't read).
  chooseYear: 'Choose the tax year on the Form 990 page.',
  // Not drawn: neither entry served (a committee recorded, the return's surface off).
  noEntry: 'Board has no Schedule O entry to draft for this tax year.',
  // Not drawn: the route isn't served (an older backend, or the module off).
  notServed: 'The Schedule O draft isn’t available yet.',
  // Not drawn: a failed read; "Try again" is the Form 990 page's.
  loadFailed: 'We couldn’t load the Schedule O draft just now.',
};
